import { z } from "zod";
import { IsoDateTimeSchema } from "./primitives.js";
export const MemoryCandidateStatusSchema = z.enum([
    "pending",
    "confirmed",
    "rejected",
    "deleted",
]);
export const MemoryChoiceSchema = z.enum(["once", "durable"]);
export const MemoryCandidateSchema = z.object({
    id: z.string(),
    candidateType: z.string(),
    candidateValue: z.string(),
    provenanceEventId: z.string().nullable(),
    confidence: z.number().min(0).max(1),
    proposedEffectDescription: z.string(),
    choice: MemoryChoiceSchema.nullable(),
    status: MemoryCandidateStatusSchema,
    createdAt: IsoDateTimeSchema,
});
// GET /v1/memory-candidates response
export const MemoryCandidatesResponseSchema = z.object({
    candidates: z.array(MemoryCandidateSchema),
});
// POST /v1/memory-candidates/{id}/confirm request
export const MemoryCandidateConfirmRequestSchema = z.object({
    choice: MemoryChoiceSchema,
});
// POST /v1/memory-candidates/{id}/reject request — no body fields required,
// but the endpoint still declares Idempotency-Key per the mutation envelope.
export const MemoryCandidateRejectRequestSchema = z.object({}).strict();
export const MemoryCandidateActionResponseSchema = MemoryCandidateSchema;
// POST /v1/memory-candidates request — propose_memory's wire shape (also
// reused as ProposeMemoryInputSchema in tools.ts, single source of truth).
// No `origin` field: this creation path is Amy-only in the MVP (T15 rules
// out UI/NLP-heuristic candidate creation from meal-outcome free text), so
// the server sets origin itself rather than trusting a client-supplied
// value.
/**
 * Closed vocabulary (Fable-5 review finding 2): a free-form candidateType
 * bucketed unknown values to "other", which the generator scored as a
 * POSITIVE weight — a "never_eat"-typed candidate would have boosted the
 * ingredient it meant to avoid. Exclusion semantics belong in hard
 * constraints, not memory; every type here either maps to an explicit
 * scoring sign OR is explicitly sign-LESS (personal_note/tone_preference:
 * ADR-017 dossier entries that color conversation only — confirmDurable
 * writes no soft_preferences row for them and the generator never sees
 * them).
 */
export const MemoryCandidateTypeSchema = z.enum([
    "ingredient_like",
    "ingredient_dislike",
    "recipe_like",
    "recipe_dislike",
    "cuisine",
    "cuisine_preference",
    "ingredient_preference",
    "personal_note",
    "tone_preference",
]);
export const MemoryCandidateCreateRequestSchema = z.object({
    candidateType: MemoryCandidateTypeSchema,
    candidateValue: z.string(),
    provenanceEventId: z.string().nullable(),
    confidence: z.number().min(0).max(1),
    proposedEffectDescription: z.string(),
});
// DELETE /v1/learned-preferences/{id} response
export const LearnedPreferenceDeleteResponseSchema = z.object({
    id: z.string(),
    deleted: z.literal(true),
    deletedAt: IsoDateTimeSchema,
});
// A confirmed, durable learned preference (Memory Center list item).
// `kind` carries the same candidateType vocabulary a memory_candidates row
// was proposed with (e.g. "ingredient_dislike") — the frontend derives the
// human "effect" copy from it locally via i18n keys rather than the server
// shipping pre-rendered prose (see frontend/src/routes/more/MemoryPage.tsx).
export const LearnedPreferenceSchema = z.object({
    id: z.string(),
    kind: z.string(),
    text: z.string(),
    origin: z.string(),
    confidence: z.number().min(0).max(1),
    createdAt: IsoDateTimeSchema,
});
// GET /v1/learned-preferences response
export const LearnedPreferencesResponseSchema = z.object({
    preferences: z.array(LearnedPreferenceSchema),
});
// ---------------------------------------------------------------------------
// GET /v1/context/dossier (ADR-017) — the per-user dossier Amy injects each
// session: confirmed notes + deterministic progress/activity digests. Every
// field is either a user-confirmed row or pure arithmetic over the user's
// own rows; nothing here is model-written or unconfirmed.
// ---------------------------------------------------------------------------
export const DossierNoteSchema = z.object({
    id: z.string(),
    kind: MemoryCandidateTypeSchema,
    text: z.string(),
    createdAt: IsoDateTimeSchema,
});
export const DossierResponseSchema = z.object({
    notes: z.array(DossierNoteSchema),
    /** Pure arithmetic over meal_outcomes/savings_events/plan_revisions — never stored. */
    progressDigest: z.object({
        mealsPlannedThisWeek: z.number().int().nonnegative(),
        outcomesRecordedThisWeek: z.number().int().nonnegative(),
        outcomesRecordedTotal: z.number().int().nonnegative(),
        currentStreakDays: z.number().int().nonnegative(),
        estimatedSavingsEurCents: z.number().int().nonnegative(),
        savingsStatus: z.literal("estimated"),
    }),
    /** Deterministic "what happened recently" — from the user's own rows, no model writing (review blocker 2). */
    activityDigest: z.object({
        lastOutcomeAt: IsoDateTimeSchema.nullable(),
        lastPlanChangeAt: IsoDateTimeSchema.nullable(),
        recentEvents: z.array(z.string()).max(5),
    }),
});
// ---------------------------------------------------------------------------
// GET /v1/amy/history — Amy chat history restore (2026-08 UX batch). Reads
// AgentCore Memory (short-term conversational events), never a separate
// store: the transcript shown here is exactly what Amy's own turn context
// persists, namespaced by the caller's actor id, across every session for
// this user within the retention window. Retention decision: last 30 days,
// capped at the 50 most recent messages, oldest-first for display. Photo
// attachments are never persisted as image bytes (patterns/bitebalance-agent
// redacts them to a literal "[photo]" text placeholder before the event is
// written) — this endpoint returns that placeholder text verbatim, it does
// not reconstruct or fabricate the photo.
// ---------------------------------------------------------------------------
export const AmyHistoryMessageSchema = z.object({
    role: z.enum(["user", "assistant"]),
    content: z.string(),
    at: IsoDateTimeSchema,
});
export const AmyHistoryResponseSchema = z.object({
    messages: z.array(AmyHistoryMessageSchema),
    /** False only when agent memory itself is unreachable/misconfigured — an honest degraded state, never a silently empty list presented as "no history". */
    available: z.boolean(),
});
// ---------------------------------------------------------------------------
// GET/POST /v1/amy/conversation — #106 (ADR-045): server-authoritative
// durable conversation identity. SAME BiteBalance conversation = SAME
// conversationId across tab close, browser restart, container rotation,
// behavior deploys, tabs, and devices; client storage is only a cache.
// GET returns (and if needed creates) the caller's ACTIVE conversation:
//   - "registered": an active row already existed
//   - "adopted": no row existed; the caller's most recently active
//     AgentCore Memory session id was adopted, so pre-#106 pilot chats
//     keep their memory thread with zero client involvement
//   - "minted": no row and no prior memory — a fresh server-side id
// POST archives the active conversation and mints a new one (a NEW chat
// gets a NEW id; conversation-native memory never leaks between them).
// The registry accepts NO client-supplied conversationId anywhere —
// ownership is server-derived from the JWT, and AgentCore Memory's actor
// scoping isolates threads per user regardless.
// ---------------------------------------------------------------------------
export const AmyConversationResponseSchema = z.object({
    conversationId: z.string().min(1).max(256),
    source: z.enum(["registered", "adopted", "minted"]),
});
//# sourceMappingURL=memory.js.map